const SELF_HOSTED_STYLE_URL = process.env.REACT_APP_MAP_STYLE_URL;
const SELF_HOSTED_ORTHO_TILE_URL = process.env.REACT_APP_MAP_ORTHO_TILE_URL;

export const MEDELLIN_VECTOR_TILE_URL =
  "https://www.medellin.gov.co/servidormapas/rest/services/Hosted/CartografiaBase_ZER_VectorTile/VectorTileServer";

export const MEDELLIN_ORTHO_2024_TILE_URL =
  SELF_HOSTED_ORTHO_TILE_URL ||
  "https://www.medellin.gov.co/servidormapas/rest/services/ServiciosImagen/Ortofoto_Medellin_2024/ImageServer/exportImage?bbox={bbox-epsg-3857}&bboxSR=3857&imageSR=3857&size=256,256&format=png32&transparent=true&f=image&interpolation=RSP_BilinearInterpolation&renderingRule=%7B%22rasterFunction%22%3A%22Ortofoto_2024_3%22%7D";

export const BASEMAP_OPTIONS = [
  {
    value: "ortofoto",
    label: "Satélite",
    description: "Imagen aérea de Medellín",
  },
  {
    value: "vibrante",
    label: "Mapa",
    description: "Calles, barrios y puntos de referencia",
  },
  {
    value: "relieve",
    label: "Relieve",
    description: "Terreno y elevación",
  },
];

function resolveStyleUrl(tileServices) {
  return tileServices?.style_url || SELF_HOSTED_STYLE_URL;
}

function resolveOrthoTileUrl(tileServices) {
  return tileServices?.ortho_tile_url || MEDELLIN_ORTHO_2024_TILE_URL;
}

function resolveGlyphsUrl(tileServices) {
  const styleUrl = resolveStyleUrl(tileServices);
  if (!styleUrl) {
    return "/fonts/{fontstack}/{range}.pbf";
  }

  try {
    return new URL("/fonts/{fontstack}/{range}.pbf", styleUrl).toString();
  } catch (error) {
    return "/fonts/{fontstack}/{range}.pbf";
  }
}

function baseStyleSkeleton(sources, layers, glyphs = null) {
  const style = {
    version: 8,
    name: "Cohuertas Medellin",
    sources,
    layers,
  };

  if (glyphs) {
    style.glyphs = glyphs;
  }

  return style;
}

function vectorBaseSources() {
  return {
    esri: {
      type: "vector",
      url: MEDELLIN_VECTOR_TILE_URL,
      attribution: "Alcaldia de Medellin",
    },
  };
}

function vectorUrbanLayers() {
  return [
    {
      id: "background",
      type: "background",
      paint: {
        "background-color": "#F5FAF0",
      },
    },
    {
      id: "municipios",
      type: "fill",
      source: "esri",
      "source-layer": "Municipios Gobant",
      minzoom: 9,
      maxzoom: 15,
      paint: {
        "fill-color": "#F7F3EA",
        "fill-outline-color": "#C7D0C1",
      },
    },
    {
      id: "espacio-publico",
      type: "fill",
      source: "esri",
      "source-layer": "EspacioPublico_Existente",
      minzoom: 8,
      paint: {
        "fill-color": "#BFEA84",
        "fill-opacity": 0.88,
        "fill-outline-color": "#6FA53E",
      },
    },
    {
      id: "rios",
      type: "fill",
      source: "esri",
      "source-layer": "Rios",
      minzoom: 8,
      paint: {
        "fill-color": "#7EC9FF",
      },
    },
    {
      id: "hidrografia",
      type: "line",
      source: "esri",
      "source-layer": "Hidrografia POT",
      minzoom: 13,
      paint: {
        "line-color": "#409DE9",
        "line-width": 1.55,
      },
    },
    {
      id: "red-hidrica",
      type: "line",
      source: "esri",
      "source-layer": "RedHidrica",
      minzoom: 13,
      paint: {
        "line-color": "#4FB7EE",
        "line-width": 1.25,
        "line-opacity": 0.82,
      },
    },
    {
      id: "manzana",
      type: "fill",
      source: "esri",
      "source-layer": "Manzana",
      minzoom: 11,
      paint: {
        "fill-color": "#FFF8EF",
        "fill-outline-color": "#FFFFFF",
      },
    },
    {
      id: "recreacion",
      type: "fill",
      source: "esri",
      "source-layer": "Recreacion y Paisajismo",
      filter: ["==", "_symbol", 0],
      minzoom: 17,
      paint: {
        "fill-color": "#98C98B",
        "fill-opacity": 0.72,
      },
    },
    {
      id: "equipamientos-educacion",
      type: "fill",
      source: "esri",
      "source-layer": "Equipamientos",
      filter: ["==", "_symbol", 0],
      minzoom: 14,
      paint: {
        "fill-color": "#F2D6B5",
        "fill-opacity": 0.7,
      },
    },
    {
      id: "equipamientos-deporte",
      type: "fill",
      source: "esri",
      "source-layer": "Equipamientos",
      filter: ["==", "_symbol", 1],
      minzoom: 14,
      paint: {
        "fill-color": "#F6E59B",
        "fill-opacity": 0.72,
      },
    },
    {
      id: "equipamientos-salud",
      type: "fill",
      source: "esri",
      "source-layer": "Equipamientos",
      filter: ["==", "_symbol", 2],
      minzoom: 14,
      paint: {
        "fill-color": "#F1C5C0",
        "fill-opacity": 0.74,
      },
    },
    {
      id: "vias-nacionales-shadow",
      type: "line",
      source: "esri",
      "source-layer": "Vias Nacionales",
      filter: ["==", "_symbol", 0],
      minzoom: 8,
      paint: {
        "line-color": "#D19134",
        "line-width": 5,
        "line-opacity": 0.45,
      },
    },
    {
      id: "vias-nacionales",
      type: "line",
      source: "esri",
      "source-layer": "Vias Nacionales",
      filter: ["==", "_symbol", 0],
      minzoom: 8,
      paint: {
        "line-color": "#FFE068",
        "line-width": 3.7,
      },
    },
    {
      id: "vias-urbanorural-shadow",
      type: "line",
      source: "esri",
      "source-layer": "Vias_UrbanoRural",
      filter: ["==", "_symbol", 1],
      minzoom: 14,
      maxzoom: 16,
      paint: {
        "line-color": "#E0A38E",
        "line-width": 4.6,
        "line-opacity": 0.72,
      },
    },
    {
      id: "vias-urbanorural",
      type: "line",
      source: "esri",
      "source-layer": "Vias_UrbanoRural",
      filter: ["==", "_symbol", 0],
      minzoom: 14,
      maxzoom: 16,
      paint: {
        "line-color": "#FFF3DE",
        "line-width": 2.35,
      },
    },
    {
      id: "metro-a",
      type: "line",
      source: "esri",
      "source-layer": "Lineas_Sistema",
      filter: ["==", "_symbol", 0],
      minzoom: 11,
      paint: {
        "line-color": "#1163E0",
        "line-width": 4.6,
      },
    },
    {
      id: "metro-b",
      type: "line",
      source: "esri",
      "source-layer": "Lineas_Sistema",
      filter: ["==", "_symbol", 1],
      minzoom: 11,
      paint: {
        "line-color": "#FF8B2A",
        "line-width": 4.6,
      },
    },
    {
      id: "metrocable",
      type: "line",
      source: "esri",
      "source-layer": "Lineas_Sistema",
      filter: ["==", "_symbol", 2],
      minzoom: 11,
      paint: {
        "line-color": "#FFD43B",
        "line-width": 2.8,
      },
    },
    {
      id: "barrios-amva",
      type: "line",
      source: "esri",
      "source-layer": "Barrios_AMVA",
      minzoom: 13,
      paint: {
        "line-color": "#D8B29D",
        "line-width": 1,
        "line-opacity": 0.58,
      },
    },
    {
      id: "barrio-vereda",
      type: "line",
      source: "esri",
      "source-layer": "BarrioVereda",
      minzoom: 13,
      paint: {
        "line-color": "#D8C2B6",
        "line-width": 0.9,
        "line-opacity": 0.45,
      },
    },
    {
      id: "lotes",
      type: "line",
      source: "esri",
      "source-layer": "Lotes o Predios",
      minzoom: 15,
      paint: {
        "line-color": "#D9D4CB",
        "line-width": 0.45,
        "line-opacity": 0.7,
      },
    },
    {
      id: "construccion-sombra",
      type: "fill",
      source: "esri",
      "source-layer": "Construccion",
      minzoom: 16,
      paint: {
        "fill-color": "rgba(120,105,92,0.20)",
        "fill-translate": [-2, 2],
      },
    },
    {
      id: "construccion-fill",
      type: "fill",
      source: "esri",
      "source-layer": "Construccion",
      minzoom: 16,
      paint: {
        "fill-color": "#F8F4EA",
      },
    },
    {
      id: "construccion-line",
      type: "line",
      source: "esri",
      "source-layer": "Construccion",
      minzoom: 16,
      paint: {
        "line-color": "#CBC4B8",
        "line-width": 0.55,
      },
    },
  ];
}

function vectorReliefLayers() {
  return [
    {
      id: "background",
      type: "background",
      paint: {
        "background-color": "#DEE7D8",
      },
    },
    {
      id: "municipios",
      type: "fill",
      source: "esri",
      "source-layer": "Municipios Gobant",
      minzoom: 9,
      maxzoom: 15,
      paint: {
        "fill-color": "#F1EFE7",
        "fill-outline-color": "#BAC6B3",
      },
    },
    {
      id: "espacio-publico",
      type: "fill",
      source: "esri",
      "source-layer": "EspacioPublico_Existente",
      minzoom: 8,
      paint: {
        "fill-color": "#9FBE77",
        "fill-opacity": 0.58,
        "fill-outline-color": "#6D8F52",
      },
    },
    {
      id: "rios",
      type: "fill",
      source: "esri",
      "source-layer": "Rios",
      minzoom: 8,
      paint: {
        "fill-color": "#718CAA",
      },
    },
    {
      id: "hidrografia",
      type: "line",
      source: "esri",
      "source-layer": "Hidrografia POT",
      minzoom: 13,
      paint: {
        "line-color": "#587A98",
        "line-width": 1.5,
      },
    },
    {
      id: "manzana",
      type: "fill",
      source: "esri",
      "source-layer": "Manzana",
      minzoom: 11,
      paint: {
        "fill-color": "#E2D7C2",
        "fill-outline-color": "#F9F7F0",
      },
    },
    {
      id: "vias-nacionales-shadow",
      type: "line",
      source: "esri",
      "source-layer": "Vias Nacionales",
      filter: ["==", "_symbol", 0],
      minzoom: 8,
      paint: {
        "line-color": "#6E5E39",
        "line-width": 5.2,
        "line-opacity": 0.65,
      },
    },
    {
      id: "vias-nacionales",
      type: "line",
      source: "esri",
      "source-layer": "Vias Nacionales",
      filter: ["==", "_symbol", 0],
      minzoom: 8,
      paint: {
        "line-color": "#C7A653",
        "line-width": 3.7,
      },
    },
    {
      id: "vias-urbanorural-shadow",
      type: "line",
      source: "esri",
      "source-layer": "Vias_UrbanoRural",
      filter: ["==", "_symbol", 1],
      minzoom: 14,
      maxzoom: 16,
      paint: {
        "line-color": "#7E665C",
        "line-width": 4.9,
        "line-opacity": 0.88,
      },
    },
    {
      id: "vias-urbanorural",
      type: "line",
      source: "esri",
      "source-layer": "Vias_UrbanoRural",
      filter: ["==", "_symbol", 0],
      minzoom: 14,
      maxzoom: 16,
      paint: {
        "line-color": "#E4D4B9",
        "line-width": 2.45,
      },
    },
    {
      id: "barrios-amva",
      type: "line",
      source: "esri",
      "source-layer": "Barrios_AMVA",
      minzoom: 13,
      paint: {
        "line-color": "#A99989",
        "line-width": 1.1,
        "line-opacity": 0.68,
      },
    },
    {
      id: "construccion-sombra",
      type: "fill",
      source: "esri",
      "source-layer": "Construccion",
      minzoom: 16,
      paint: {
        "fill-color": "rgba(62,53,43,0.34)",
        "fill-translate": [-3, 3],
      },
    },
    {
      id: "construccion-fill",
      type: "fill",
      source: "esri",
      "source-layer": "Construccion",
      minzoom: 16,
      paint: {
        "fill-color": "#CCBEA8",
      },
    },
    {
      id: "construccion-line",
      type: "line",
      source: "esri",
      "source-layer": "Construccion",
      minzoom: 16,
      paint: {
        "line-color": "#8D7F6B",
        "line-width": 0.7,
      },
    },
  ];
}

function orthophotoLayers() {
  return [
    {
      id: "background",
      type: "background",
      paint: {
        "background-color": "#E7F0E3",
      },
    },
    {
      id: "orto-2024",
      type: "raster",
      source: "ortofoto-2024",
      minzoom: 9,
      maxzoom: 20,
      paint: {
        "raster-saturation": 0.14,
        "raster-contrast": 0.12,
        "raster-brightness-min": 0.07,
        "raster-brightness-max": 0.95,
      },
    },
    {
      id: "vias-nacionales-overlay",
      type: "line",
      source: "esri",
      "source-layer": "Vias Nacionales",
      filter: ["==", "_symbol", 0],
      minzoom: 9,
      paint: {
        "line-color": "#FFF5D4",
        "line-width": 2.6,
        "line-opacity": 0.74,
      },
    },
    {
      id: "vias-urbanorural-overlay",
      type: "line",
      source: "esri",
      "source-layer": "Vias_UrbanoRural",
      filter: ["==", "_symbol", 0],
      minzoom: 14,
      maxzoom: 16,
      paint: {
        "line-color": "#FFFFFF",
        "line-width": 1.25,
        "line-opacity": 0.5,
      },
    },
    {
      id: "metro-a-overlay",
      type: "line",
      source: "esri",
      "source-layer": "Lineas_Sistema",
      filter: ["==", "_symbol", 0],
      minzoom: 11,
      paint: {
        "line-color": "#1E63F0",
        "line-width": 3.2,
        "line-opacity": 0.82,
      },
    },
    {
      id: "metro-b-overlay",
      type: "line",
      source: "esri",
      "source-layer": "Lineas_Sistema",
      filter: ["==", "_symbol", 1],
      minzoom: 11,
      paint: {
        "line-color": "#F58C2D",
        "line-width": 3.2,
        "line-opacity": 0.82,
      },
    },
    {
      id: "metrocable-overlay",
      type: "line",
      source: "esri",
      "source-layer": "Lineas_Sistema",
      filter: ["==", "_symbol", 2],
      minzoom: 11,
      paint: {
        "line-color": "#F7C32A",
        "line-width": 2.2,
        "line-opacity": 0.9,
      },
    },
    {
      id: "barrios-overlay",
      type: "line",
      source: "esri",
      "source-layer": "Barrios_AMVA",
      minzoom: 13,
      paint: {
        "line-color": "#E9F1E4",
        "line-width": 0.9,
        "line-opacity": 0.34,
      },
    },
    {
      id: "construccion-overlay",
      type: "line",
      source: "esri",
      "source-layer": "Construccion",
      minzoom: 16,
      paint: {
        "line-color": "#FFFFFF",
        "line-width": 0.45,
        "line-opacity": 0.28,
      },
    },
  ];
}

export function buildBasemapStyle(mode) {
  const glyphs = resolveGlyphsUrl();

  if (SELF_HOSTED_STYLE_URL && mode !== "ortofoto") {
    return SELF_HOSTED_STYLE_URL;
  }

  if (mode === "ortofoto") {
    return baseStyleSkeleton(
      {
        ...vectorBaseSources(),
        "ortofoto-2024": {
          type: "raster",
          tiles: [MEDELLIN_ORTHO_2024_TILE_URL],
          tileSize: 256,
          attribution: "Alcaldia de Medellin · Ortofoto 2024",
        },
      },
      orthophotoLayers(),
      glyphs,
    );
  }

  if (mode === "relieve") {
    return baseStyleSkeleton(vectorBaseSources(), vectorReliefLayers(), glyphs);
  }

  return baseStyleSkeleton(vectorBaseSources(), vectorUrbanLayers(), glyphs);
}

export function buildBasemapStyleRuntime(mode, tileServices = null) {
  const runtimeStyleUrl = tileServices?.style_url || SELF_HOSTED_STYLE_URL;
  const runtimeOrthoTileUrl = tileServices?.ortho_tile_url || MEDELLIN_ORTHO_2024_TILE_URL;
  const glyphs = resolveGlyphsUrl(tileServices);

  if (runtimeStyleUrl && mode !== "ortofoto") {
    return runtimeStyleUrl;
  }

  if (mode === "ortofoto") {
    return baseStyleSkeleton(
      {
        ...vectorBaseSources(),
        "ortofoto-2024": {
          type: "raster",
          tiles: [runtimeOrthoTileUrl],
          tileSize: 256,
          attribution: "Alcaldia de Medellin Â· Ortofoto 2024",
        },
      },
      orthophotoLayers(),
      glyphs,
    );
  }

  if (mode === "relieve") {
    return baseStyleSkeleton(vectorBaseSources(), vectorReliefLayers(), glyphs);
  }

  return baseStyleSkeleton(vectorBaseSources(), vectorUrbanLayers(), glyphs);
}

export function supportsBuildingExtrusion(mode) {
  return mode !== "ortofoto";
}

export function getTerrainExaggeration(mode) {
  if (mode === "relieve") {
    return 1.58;
  }
  if (mode === "ortofoto") {
    return 1;
  }
  return 1.04;
}
